import { useTranslation } from 'react-i18next';
import { AlertTriangle, Inbox, Lock, RefreshCw, SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

/** Shared frame so every empty/error/forbidden state sits identically on the page. */
const Centered = ({ icon: Icon, title, body, action, tone = 'muted' }) => (
  <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
    <div
      className={cn(
        'flex size-11 items-center justify-center rounded-full',
        tone === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground',
      )}
    >
      <Icon className="size-5" />
    </div>
    <div className="space-y-1">
      <p className="font-medium">{title}</p>
      {body ? <p className="max-w-md text-sm text-muted-foreground">{body}</p> : null}
    </div>
    {action}
  </div>
);

export const EmptyState = ({ title, body, action, icon = Inbox }) => {
  const { t } = useTranslation('common');
  return (
    <Centered
      icon={icon}
      title={title || t('table.noResults')}
      body={body ?? t('table.noResultsHint')}
      action={action}
    />
  );
};

export const NoResultsState = (props) => <EmptyState icon={SearchX} {...props} />;

/**
 * Errors show an inline retry, never a blank page (docs/04 §6). The request id is
 * surfaced because it is the only handle support has to find the failure in the logs.
 */
export const ErrorState = ({ error, onRetry }) => {
  const { t } = useTranslation('common');
  const message = error?.status === 0 ? t('errors.network') : error?.message || t('errors.generic');

  return (
    <Centered
      icon={AlertTriangle}
      tone="destructive"
      title={t('errors.title')}
      body={
        <>
          {message}
          {error?.requestId ? (
            <span className="mt-1 block font-mono text-caption opacity-70">
              {t('errors.requestId', { id: error.requestId })}
            </span>
          ) : null}
        </>
      }
      action={
        onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="size-4" />
            {t('actions.retry')}
          </Button>
        ) : null
      }
    />
  );
};

/**
 * A 403 must explain *which* permission is missing (docs/04 §6) — "access denied" with
 * no name sends the user to an administrator who cannot act on it either.
 */
export const ForbiddenState = ({ permission }) => {
  const { t } = useTranslation('common');
  return (
    <Centered
      icon={Lock}
      title={t('errors.forbidden')}
      body={permission ? t('errors.forbiddenBody', { permission }) : t('errors.forbiddenBodyGeneric')}
    />
  );
};

/** Skeleton rows matching the final table layout — never a spinner over a grid. */
export const TableSkeleton = ({ rows = 8, columns = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <TableRow key={rowIndex} aria-hidden="true">
        {Array.from({ length: columns }).map((__, colIndex) => (
          <TableCell key={colIndex}>
            <Skeleton className={cn('h-4', colIndex === 0 ? 'w-24' : 'w-full max-w-32')} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

export const CardSkeleton = ({ className }) => (
  <div className={cn('space-y-3 rounded-lg border bg-card p-5', className)}>
    <Skeleton className="h-3 w-24" />
    <Skeleton className="h-7 w-36" />
    <Skeleton className="h-3 w-20" />
  </div>
);

/*
 * `p-7` and `space-y-6` deliberately mirror `<Page>` — the skeleton is only doing its job
 * if the real content lands on the same grid it was drawn on. At `p-6` every lazy route
 * nudged its own heading sideways the moment the chunk arrived.
 */
export const PageSkeleton = () => (
  <div className="space-y-6 p-7">
    <div className="space-y-2">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-72" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
    <Skeleton className="h-64 w-full rounded-lg" />
  </div>
);
